import type { SearchDocument, SearchHighlight } from '../../../entities/search';
import { type CollectionUiConfig, slotHighlightKey, slotLabel, slotValue } from '../../constants/collections';
import { type SnippetSegment, splitHighlightSnippet } from './highlightSnippet';

/**
 * One resolved line-2 slot, ready to render (or persist, once `highlight` is
 * dropped). This is the serializable slot shape a recent round-trips.
 *
 *   selectSlots(config, doc, highlight)  ─▶  ResolvedSlot[]  (≤ 3)
 *     1. the slot whose highlightKey MATCHED leads, marked
 *     2. fill by insertion order, skipping slots with no value
 *     3. drop a slot that just echoes the identifier
 *     4. dedupe by field AND by value, cap at 3
 */
export interface ResolvedSlot {
  /** The map key (the engine field) — the stable slot identity. */
  field: string;
  label: string;
  value: string;
  /** Marked segments when this slot's field matched the query; absent otherwise. */
  highlight?: SnippetSegment[];
}

const MAX_SLOTS = 3;

/**
 * Build the ≤3 line-2 slots for a result. Pure — no React, no DOM — so it is
 * unit-testable in isolation. The matched slot (if any) is promoted to the front
 * carrying its `<mark>`-split snippet; the rest fill by the registry's insertion
 * order, skipping any slot the document leaves empty, any slot that just echoes
 * the line-1 identifier, and any slot whose value duplicates one already taken.
 *
 * `highlight` is empty for recents (no live match), so those fall straight
 * through to "first 3 populated slots by order", unhighlighted.
 */
export function selectSlots(
  config: CollectionUiConfig,
  doc: SearchDocument,
  highlight: SearchHighlight
): ResolvedSlot[] {
  const entries = Object.entries(config.slots);
  const identifier = config.identifier(doc)?.trim();

  // The slot the query matched, if any: the first registered slot whose
  // highlight key carries an engine snippet. Identifier matches (or recents)
  // leave this null, so selection is pure insertion order.
  const matchedField = entries.find(([field, def]) => {
    const snippet = highlight[slotHighlightKey(field, def)]?.snippet;
    return typeof snippet === 'string' && snippet.includes('<mark>');
  })?.[0];

  const ordered = matchedField ? [matchedField, ...entries.map(([field]) => field)] : entries.map(([field]) => field);

  const slots: ResolvedSlot[] = [];
  // Field guard stops the promoted matched slot rendering twice; value guard
  // catches two DIFFERENT fields carrying identical text (e.g. counterpartyName
  // and reference holding the same string).
  const seenFields = new Set<string>();
  const seenValues = new Set<string>();
  for (const field of ordered) {
    if (seenFields.has(field) || slots.length >= MAX_SLOTS) continue;
    const def = config.slots[field];
    const value = slotValue(field, def, doc);
    if (value === undefined) continue;
    seenFields.add(field);

    const trimmed = value.trim();
    if (identifier && trimmed === identifier) continue; // don't echo the identifier
    const fingerprint = trimmed.toLowerCase();
    if (seenValues.has(fingerprint)) continue;
    seenValues.add(fingerprint);

    const snippet = field === matchedField ? highlight[slotHighlightKey(field, def)]?.snippet : undefined;
    slots.push({
      field,
      label: slotLabel(def, doc),
      value,
      ...(snippet ? { highlight: splitHighlightSnippet(snippet) } : {}),
    });
  }
  return slots;
}
